import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { Params } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import * as fromFilters from '~app/modules/filters/store';
import * as fromCatalog from '~app/modules/catalog/store';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { SearchApplicationEvent, SortOption, SortOptionsEnum } from '~shared/modules/application-event/models';
import { ApplicationSearchEventSource } from '~shared/modules/application-event/constants';
import { Category } from '~app/modules/generic/models';
import { getCategoryParents } from '~app/modules/generic/store';
import { DEFAULT_LIMIT_OF_PRODUCTS } from '~app/modules/catalog/constants/catalog-products-list.constants';
import { AttributeOption } from '~shared/models';
import { FILTER_ATTRIBUTE_KEYS } from '~app/modules/filters/constants';
import { getSortOption } from '~app/modules/catalog/store';
import { MEDIA_ALISES } from '~shared/constants/media.constant';
import { TooltipPositions } from '~shared/modules/tooltip/models/tooltip-positions.model';
import { MediaObserver } from '@angular/flex-layout';
import { Attribute } from '~app/modules/filters/models';
import { PlpVariantRestyleService } from '~shared/services/plp-variant-restyle/plp-variant-restyle.service';

@Component({
  selector: 'app-search-catalog-toolbar',
  templateUrl: './catalog-toolbar.component.html',
  styleUrls: ['./catalog-toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CatalogToolbarComponent implements OnChanges, OnDestroy {
  @Input() searchTerm: string;
  @Input() searchCorrection: string;
  @Input() title: string;
  @Input() queryParams: Params;
  @Input() customTitle: string;
  @Input() totalCount: number;
  @Input() selectedCategory: Category;
  @Input() mustDisplayTitle: boolean = true;

  selectedOption$: Observable<SortOption>;
  bulletTextColor = '#99ABC7'; // white
  bulletBackgroundColor = '#ffff'; // grey-tint-80
  bestMatchSortOption = SortOptionsEnum.BEST_MATCH;
  isPLP: boolean;
  isNewPLPVariantRestyle: boolean;

  get brandTitle(): string {
    const isEmptyBrandList = !(this.brands && this.brands.length);

    if (this.searchTerm || isEmptyBrandList) {
      return null;
    }

    const { filter: { options = {} } = {} } = this.queryParams || {};

    //Make sure that the only options/filters we have is the brand
    //Also it's one brand only
    const isOneBrandFilterApplied = options.brand && options.brand.length === 1;

    if (!isOneBrandFilterApplied) {
      return null;
    }

    const firstBrand = options.brand[0];
    const selectedBrand: AttributeOption = this.brands.find((brand) => {
      return brand.id === firstBrand;
    });

    //This should not be the case. However, it's a template function. So, if it failed, it will break the whole website
    // I am just making sure that it will never throw an exception
    if (!selectedBrand) {
      return null;
    }

    return selectedBrand.name;
  }

  hasFilters$: Observable<boolean>;
  categoryParents$: Observable<Category[]>;
  defaultProductsLimit = DEFAULT_LIMIT_OF_PRODUCTS;
  tooltipPosition = TooltipPositions.center;

  private unsubscribe$ = new Subject();
  private brands: AttributeOption[];

  constructor(
    private store: Store<fromFilters.State>,
    private cd: ChangeDetectorRef,
    private applicationEventService: ApplicationEventService,
    private mediaObserver: MediaObserver,
    private plpVariantRestyleService: PlpVariantRestyleService
  ) {
    this.hasFilters$ = this.store.pipe(
      select(fromCatalog.hasFilters),
      tap(() => {
        this.cd.markForCheck();
      })
    );

    this.subscribeToBrands();
    this.initializeCategoryParents();
    this.selectedOption$ = this.store.pipe(select(getSortOption));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchCorrection'] && changes['searchCorrection'].currentValue) {
      this.searchCorrection = changes['searchCorrection'].currentValue;
      this.initializeAutoCorrection();
    }

    if (!changes['queryParams'] || !changes['searchTerm'] || !changes['searchCorrection']) {
      return;
    }
  }

  ngOnInit() {
    this.plpVariantRestyleService
      .isNewPLPVariantRestyle()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((value) => {
        this.isNewPLPVariantRestyle = value;
      });
    this.isPLP = this.plpVariantRestyleService.isPLP();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private subscribeToBrands(): void {
    this.store
      .pipe(select(fromFilters.getAttributes))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((attributes) => {
        if (attributes && attributes.length) {
          const brandAttribute: Attribute = attributes.find(
            (attribute) => attribute.id === FILTER_ATTRIBUTE_KEYS.BRAND
          );
          this.brands = brandAttribute?.options;
          // Check the template variables, maybe it's a brand page!!
          this.cd.markForCheck();
        }
      });
  }

  private initializeAutoCorrection(): void {
    if (!this.searchCorrection || !this.searchTerm) {
      return;
    }

    this.applicationEventService.publish(
      new SearchApplicationEvent(this.searchTerm, ApplicationSearchEventSource.AutoCorrection, this.searchCorrection)
    );
    this.cd.markForCheck();
  }

  private initializeCategoryParents(): void {
    this.categoryParents$ = this.store.pipe(
      select(getCategoryParents),
      map((categories) => categories.slice().reverse()),
      tap(() => this.cd.markForCheck())
    );
  }
}
